import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiFillFacebook, AiFillInstagram } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";
import { loadUser } from "../../redux/actions/user";
import { server } from "../../server";
import { getLinkedSocialEntry } from "../../utils/socialProfileLinks";

const platformMeta = {
  facebook: {
    label: "Facebook",
    Icon: AiFillFacebook,
    placeholder: "https://www.facebook.com/your.profile or username",
    help: "Add the link to your Facebook profile (or your Facebook username).",
  },
  instagram: {
    label: "Instagram",
    Icon: AiFillInstagram,
    placeholder: "https://www.instagram.com/yourhandle or @username",
    help: "Add the link to your Instagram profile (or your Instagram username).",
  },
};

const LinkedSocialProfiles = ({
  cardClass,
  primaryButtonClasses,
  secondaryButtonClasses,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [drafts, setDrafts] = useState({ facebook: "", instagram: "" });
  const [savingPlatform, setSavingPlatform] = useState("");
  const [removingPlatform, setRemovingPlatform] = useState("");

  const linkedFacebook = getLinkedSocialEntry(user?.linkedSocialProfiles, "facebook");
  const linkedInstagram = getLinkedSocialEntry(user?.linkedSocialProfiles, "instagram");

  useEffect(() => {
    setDrafts({
      facebook: linkedFacebook?.profileUrl || linkedFacebook?.username || "",
      instagram: linkedInstagram?.profileUrl || linkedInstagram?.username || "",
    });
  }, [
    linkedFacebook?.profileUrl,
    linkedFacebook?.username,
    linkedInstagram?.profileUrl,
    linkedInstagram?.username,
  ]);

  const savePlatform = async (platform) => {
    setSavingPlatform(platform);
    try {
      await axios.put(
        `${server}/user/linked-social-profiles`,
        { [platform]: drafts[platform] || "" },
        { withCredentials: true }
      );
      await dispatch(loadUser());
      toast.success(`${platformMeta[platform].label} profile saved.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Unable to save ${platformMeta[platform].label}.`
      );
    } finally {
      setSavingPlatform("");
    }
  };

  const removePlatform = async (platform) => {
    setRemovingPlatform(platform);
    try {
      await axios.put(
        `${server}/user/linked-social-profiles`,
        { [platform]: "" },
        { withCredentials: true }
      );
      await dispatch(loadUser());
      toast.success(`${platformMeta[platform].label} profile removed.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || `Unable to remove ${platformMeta[platform].label}.`);
    } finally {
      setRemovingPlatform("");
    }
  };

  return (
    <section className={cardClass}>
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Facebook &amp; Instagram
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Optional. You can add a Facebook or Instagram profile to your account so buyers can find you there.
        </p>
      </header>

      <div className="space-y-5">
        {["facebook", "instagram"].map((platform) => {
          const meta = platformMeta[platform];
          const linked = platform === "facebook" ? linkedFacebook : linkedInstagram;
          const isRemoving = removingPlatform === platform;
          const isSaving = savingPlatform === platform;

          return (
            <div
              key={platform}
              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm">
                  <meta.Icon size={20} className="text-[#38513b]" />
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{meta.label}</h3>
                    <p className="text-xs text-slate-500">{meta.help}</p>
                  </div>

                  {linked ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                      <span className="mr-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                        Added
                      </span>
                      <a
                        href={linked.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold underline hover:text-emerald-950"
                      >
                        {platform === "instagram" && linked.username
                          ? `@${linked.username}`
                          : linked.username || linked.profileUrl}
                      </a>
                    </div>
                  ) : null}

                  <div className="grid gap-2">
                    <input
                      type="text"
                      value={drafts[platform]}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [platform]: e.target.value }))
                      }
                      placeholder={meta.placeholder}
                      className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-[#38513b]"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={
                          primaryButtonClasses ||
                          "inline-flex h-10 items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white"
                        }
                        disabled={isRemoving || isSaving}
                        onClick={() => savePlatform(platform)}
                      >
                        {isSaving ? "Saving…" : linked ? "Update" : "Save"}
                      </button>
                      {linked ? (
                        <button
                          type="button"
                          className={
                            secondaryButtonClasses ||
                            "inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700"
                          }
                          disabled={isRemoving || isSaving}
                          onClick={() => removePlatform(platform)}
                        >
                          {isRemoving ? "Removing…" : "Remove"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LinkedSocialProfiles;
